import React, {
  Fragment,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  TableBody,
  TableContainer,
  TableHead,
  Typography,
  TableRow,
  TableCell,
  Collapse,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';

import { Model } from '../types';
import { MoreMenu } from '../components';
import { DataTableColumnProps } from './DataTableColumnProps';
import * as Sx from './DataTable.styles';
import { DataTableDraggableProps } from './DataTable.props';
import { StyledTable, StyledTableRow, StyledTableCell } from '.';

export const DataTableDraggable = <T extends Model>({
  children,
  data,
  menuItems,
  rowCollapseContent,
  rowCollapseOpened,
  noResultElement,
  tableBodyStyles,
  tableHeaderStyles,
  tableStyles,
  specialFirstRow,
  isRowOnError = (item: T) => false,
  customRowKey = (item: T) => item.id,
  reorderHandleColumnIndex,
  rowOnReorder,
}: DataTableDraggableProps<T>) => {
  const { t } = useTranslation();
  const [activeItem, setActiveItem] = useState<T | null>(null);

  const extractedChildren = useMemo(() => {
    const result = [];
    for (const child of children) {
      if ((child as ReactElement)?.type === Fragment) {
        const fragmentChildren = (child as ReactElement).props[
          'children'
        ] as Array<DataTableColumnProps<T>>;
        result.push(...fragmentChildren);
      } else {
        result.push(child);
      }
    }

    return result;
  }, [children]);

  const renderData = useMemo(() => data, [data]);
  const [draggingRowId, setDraggingRow] = React.useState<string | null>(null);

  /**
   * @see https://github.com/jbetancur/react-data-table-component
   */
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>, row: T) => {
      setDraggingRow(customRowKey(row));
    },
    [customRowKey]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent<HTMLDivElement>, row: T) => {
      if (!renderData || !draggingRowId) return;

      const targetRowId = customRowKey(row);
      if (!targetRowId || targetRowId === draggingRowId) return;

      const elementAt = (array: T[], getId: (a: T) => string, id: string) =>
        array.findIndex((d) => getId(d) === id);
      const draggingRowIndex = elementAt(
        renderData,
        customRowKey,
        draggingRowId
      );
      const targetRowIndex = elementAt(renderData, customRowKey, targetRowId);

      if (draggingRowIndex === -1 || targetRowIndex === -1) return;

      const draggedItem = renderData[draggingRowIndex];
      const targetItem = renderData[targetRowIndex];

      const reordered = [...renderData];
      reordered[draggingRowIndex] = targetItem;
      reordered[targetRowIndex] = draggedItem;

      rowOnReorder?.(reordered, draggedItem, targetItem);

      // When the order changes, the id may change, for example when we have the id "QuotePart1"
      setDraggingRow(customRowKey(draggedItem));
    },
    [customRowKey, draggingRowId, renderData, rowOnReorder]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDraggingRow(null);
  }, []);

  const columns = extractedChildren.filter((x) => x) as Array<
    ReactElement<DataTableColumnProps<T>>
  >;

  const hasMenuItems = useMemo(
    () => menuItems && data?.some((item) => menuItems(item)),
    [data, menuItems]
  );

  const onRowClick = useCallback(
    (dataItem: T) => {
      if (activeItem === dataItem) {
        setActiveItem(null);
      } else {
        setActiveItem(dataItem);
      }
    },
    [activeItem]
  );

  const getHeaderChildren = (child: ReactElement<DataTableColumnProps<T>>) => {
    if (child.props.tooltip && child.props.title) {
      return (
        <Tooltip title={child.props.tooltip}>
          <span>{child.props.title}</span>
        </Tooltip>
      );
    }
    return <>{child.props.title}</>;
  };

  return (
    <TableContainer sx={tableStyles}>
      <StyledTable>
        <TableHead sx={tableHeaderStyles}>
          <StyledTableRow>
            {columns &&
              columns.map((child) => (
                <StyledTableCell
                  key={child.props.property.toString()}
                  width={
                    child.props.property.toString() !== 'selectable'
                      ? child.props.width
                      : '42px'
                  }
                  sx={Sx.tableHeaderCell(child.props)}
                >
                  {getHeaderChildren(child)}
                </StyledTableCell>
              ))}
            {hasMenuItems && <StyledTableCell className="menu-items" />}
          </StyledTableRow>
          {specialFirstRow}
        </TableHead>

        <TableBody sx={{ ...tableBodyStyles }}>
          {renderData &&
            renderData.map((dataItem, index) => (
              <React.Fragment key={customRowKey(dataItem)}>
                <StyledTableRow
                  onClick={() => onRowClick(dataItem)}
                  onDragEnter={(e) => handleDragEnter(e, dataItem)}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={[
                    index % 2 === 1 ? 'alternate-color' : '',
                    activeItem === dataItem ? 'active-item' : '',
                    draggingRowId === customRowKey(dataItem)
                      ? 'dragging-row'
                      : '',
                  ].join(' ')}
                  sx={Sx.tableBodyRow(isRowOnError(dataItem))}
                >
                  {columns &&
                    columns.map((child, index) => (
                      <StyledTableCell
                        sx={Sx.tableBodyCell(
                          child.props,
                          index,
                          hasMenuItems,
                          activeItem === dataItem
                        )}
                        draggable={index === reorderHandleColumnIndex}
                        onDragStart={
                          index === reorderHandleColumnIndex
                            ? (e) => handleDragStart(e, dataItem)
                            : undefined
                        }
                        onDragEnd={
                          index === reorderHandleColumnIndex
                            ? handleDragEnd
                            : undefined
                        }
                        key={child.props.property.toString()}
                      >
                        <>
                          {index !== 0 &&
                            child.props.hasMobileLabel !== false && (
                              <Typography
                                variant="caption"
                                sx={Sx.tableBodyCellCaption(child.props)}
                              >
                                {child.props.title}
                              </Typography>
                            )}
                          {child.props.activeOutput && activeItem === dataItem
                            ? child.props.activeOutput(
                                get(dataItem, child.props.property),
                                dataItem
                              )
                            : child.props.output
                            ? child.props.output(
                                get(dataItem, child.props.property),
                                dataItem
                              )
                            : get(dataItem, child.props.property)}
                        </>
                      </StyledTableCell>
                    ))}
                  {hasMenuItems && menuItems && (
                    <StyledTableCell
                      className="menu-items"
                      sx={Sx.tableBodyCellMenuItem(!!menuItems(dataItem))}
                    >
                      <MoreMenu>{menuItems(dataItem)}</MoreMenu>
                    </StyledTableCell>
                  )}
                </StyledTableRow>

                {rowCollapseContent && rowCollapseOpened && (
                  <TableRow className="row-collapse-content">
                    <TableCell
                      style={{ padding: 0, borderBottom: 'none' }}
                      colSpan={
                        extractedChildren.length + (hasMenuItems ? 1 : 0)
                      }
                    >
                      <Collapse
                        sx={Sx.tableBodyCollapsibleCell(isRowOnError(dataItem))}
                        in={rowCollapseOpened(dataItem)}
                      >
                        {rowCollapseOpened(dataItem) &&
                          rowCollapseContent(dataItem)}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
        </TableBody>
      </StyledTable>
      {data &&
        data.length === 0 &&
        !specialFirstRow &&
        (noResultElement ? (
          noResultElement
        ) : (
          <Typography variant="h3" sx={Sx.noData}>
            {t('common.noItemsFound')}
          </Typography>
        ))}
    </TableContainer>
  );
};
